import React from "react";
import { Button } from "@material-ui/core";
import BusinessOutlined from "@material-ui/icons/BusinessOutlined";
import PeopleOutline from "@material-ui/icons/PeopleOutline";
import PersonOutline from "@material-ui/icons/PersonOutline";
import PhoneOutlined from "@material-ui/icons/PhoneOutlined";
import StorefrontOutlined from "@material-ui/icons/StorefrontOutlined";
import DescriptionOutlined from "@material-ui/icons/DescriptionOutlined";
import EventOutlined from "@material-ui/icons/EventOutlined";
import AssignmentOutlined from "@material-ui/icons/AssignmentOutlined";
import LinkOutlined from "@material-ui/icons/LinkOutlined";
import Info from "@material-ui/icons/Info";
import {
  AddressFields,
  Field,
  SameAddress,
  Section,
  StepHeader,
  useTranslation
} from "./ui";
import { SOLE_PROPRIETOR } from "../../../util/ApplicationFields";
import { getReceiptData, hasSoleProprietorEin } from "./values";

function Receipt({ application }) {
  const t = useTranslation();
  const data = getReceiptData(application);
  const now = new Date();
  return (
    <aside
      className="merchant-receipt-panel"
      aria-label={t("Live Receipt Preview")}
    >
      <h2>{t("Live Receipt Preview")}</h2>
      <p>{t("See how your customer receipts will look.")}</p>
      <div className="merchant-receipt-paper">
        <div className="merchant-receipt">
          <div className="merchant-receipt-head">
            <strong>
              {data.name === "Your Business" ? t(data.name) : data.name}
            </strong>
            <div>{data.street}</div>
            {data.suite && <div>{data.suite}</div>}
            <div>
              {data.city}, {data.state} {data.zip}
            </div>
            <div>{data.phone}</div>
            <div>{data.website}</div>
          </div>
          <hr />
          <div className="merchant-receipt-row">
            <span>{now.toLocaleDateString("en-US")}</span>
            <span>
              {now.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit"
              })}
            </span>
          </div>
          <div className="merchant-receipt-row">
            <span>Terminal ID: 123456</span>
            <span>TID: A1B2</span>
          </div>
          <div className="merchant-receipt-row">
            <span>MID: 987654321</span>
            <span>Batch: 000123</span>
          </div>
          <hr />
          <div className="merchant-receipt-sale">{t("SALE")}</div>
          <div className="merchant-receipt-row">
            <span>{t("Subtotal")}</span>
            <span>$32.50</span>
          </div>
          <div className="merchant-receipt-row">
            <span>{t("Taxes")} (7.75%)</span>
            <span>$2.52</span>
          </div>
          <hr />
          <div className="merchant-receipt-row merchant-receipt-total">
            <span>{t("TOTAL")}</span>
            <span>$35.02</span>
          </div>
          <hr />
          <div className="merchant-receipt-row">
            <span>VISA</span>
            <span>Chip Read</span>
          </div>
          <div className="merchant-receipt-row">
            <span>**** 4242</span>
            <span>Approval: 123456</span>
          </div>
          <div className="merchant-receipt-row"><span /><span>Ref: 9876543210</span></div>
          <div className="merchant-receipt-thanks">{t("Thank You!")}</div>
          <div className="merchant-receipt-again">{t("Please come again.")}</div>
        </div>
      </div>
    </aside>
  );
}

export default function Business({
  application,
  errors,
  onApplicationTextChange,
  onCheckChange
}) {
  const t = useTranslation();
  const props = { application, errors, onApplicationTextChange };
  const sole = application.ownershipType === SOLE_PROPRIETOR;
  const ein = hasSoleProprietorEin(application);
  const years = Array.from({ length: 100 }, (_, index) =>
    String(new Date().getFullYear() - index)
  );
  return (
    <div className="merchant-business">
      <div>
        <StepHeader title="Business Info">
          Let's start with some basic information about your business.
        </StepHeader>
        <div className="merchant-grid">
          <Field
            {...props}
            name="ownershipType"
            label="Ownership Type"
            required
            icon={PeopleOutline}
            options={[SOLE_PROPRIETOR, "Corporation", "LLC", "Non Profit"]}
          />
          <div className="merchant-hint">
            <Info aria-hidden="true" />
            {t("Your ownership type determines which information is required.")}
          </div>
          {sole ? (
            <>
              <Field
                {...props}
                name="firstName"
                label="First Name"
                required
                icon={PersonOutline}
              />
              <Field
                {...props}
                name="lastName"
                label="Last Name"
                required
                icon={PersonOutline}
              />
            </>
          ) : (
            <div className="merchant-full">
              <Field
                {...props}
                name="legalBusinessName"
                label="Legal Business Name"
                required
                icon={BusinessOutlined}
              />
            </div>
          )}
          {sole && (
            <div className="merchant-ein merchant-full">
              <strong>{t("Would you like to add an EIN?")}</strong>
              {["Yes", "No"].map(answer => (
                <Button
                  key={answer}
                  variant={
                    ein === (answer === "Yes") ? "contained" : "outlined"
                  }
                  color="primary"
                  aria-pressed={ein === (answer === "Yes")}
                  onClick={() =>
                    onApplicationTextChange("soleProprietorHasEin")({
                      target: { value: answer }
                    })
                  }
                >
                  {t(answer)}
                </Button>
              ))}
            </div>
          )}
          {(!sole || ein) && (
            <div className="merchant-full">
              <Field
                {...props}
                name="federalTaxId"
                label="Federal Tax ID"
                required
                icon={AssignmentOutlined}
                placeholder="00-0000000"
                inputProps={{ inputMode: "numeric" }}
              />
            </div>
          )}
          <div className="merchant-full">
            <Field
              {...props}
              name="doingBusinessAs"
              label="Doing Business As (DBA)"
              required
              icon={StorefrontOutlined}
              placeholder="Example: Kennedy's Cafe"
            />
          </div>
          <div className="merchant-full">
            <Field
              {...props}
              name="businessDescription"
              label="Business Description"
              required
              icon={DescriptionOutlined}
              placeholder="Example: Restaurant, Retail, Professional Services"
            />
          </div>
          <Field
            {...props}
            name="businessYear"
            label="What year did you open your business?"
            required
            icon={EventOutlined}
            options={years}
          />
          <Field
            {...props}
            name="website"
            label="Website URL (optional)"
            icon={LinkOutlined}
            placeholder="https://yourwebsite.com"
          />
        </div>
        <Section title="Business Address">
          <AddressFields {...props} prefix="business" />
          <div className="merchant-phone">
            <Field
              {...props}
              name="businessPhone"
              label="Business Phone Number"
              required
              icon={PhoneOutlined}
              inputProps={{ inputMode: "tel" }}
            />
          </div>
        </Section>
        <SameAddress
          application={application}
          name="mailingDifferent"
          onCheckChange={onCheckChange}
          label="Use business address as mailing address"
          badge="Recommended"
          description="Your mailing address is where you will receive all communications for your merchant account, including account information, monthly statements, and important notices."
        />
        {application.mailingDifferent && (
          <Section title="Mailing Address">
            <AddressFields {...props} prefix="mailing" />
          </Section>
        )}
        <SameAddress
          application={application}
          name="shippingDifferent"
          onCheckChange={onCheckChange}
          label="Use business address for equipment shipping"
          badge="Recommended"
          description="This address will only be used once to ship your equipment. You will receive your equipment within 2-3 business days from approval, ready and programmed to accept credit cards."
        />
        {application.shippingDifferent && (
          <Section title="Equipment Shipping Address">
            <AddressFields {...props} prefix="shipping" />
          </Section>
        )}
      </div>
      <Receipt application={application} />
    </div>
  );
}
